import DOMPurify from "dompurify";
import { marked } from "marked";
import { IcExcel } from "@racoon/icons";
import * as XLSX from "xlsx";
import { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { Namespace, useT } from "@racoon/i18n";
import { format } from "date-fns";

const RaccoonBotAvatar = "/RaccoonBotAvatar.png";

const THINKING_MESSAGE_COUNT = 6;

// Configure marked options
marked.setOptions({
  breaks: true,
  gfm: true,
  async: false,
});

// Function to extract table data from HTML by index
const extractTableDataByIndex = (html: string, tableIndex: number): string[][] => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const tables = doc.querySelectorAll("table");

  if (tableIndex >= tables.length) return [];

  const table = tables[tableIndex];
  const data: string[][] = [];

  const headers = table.querySelectorAll("thead th");
  if (headers.length > 0) {
    data.push(Array.from(headers).map((th) => th.textContent || ""));
  }

  const rows = table.querySelectorAll("tbody tr");
  rows.forEach((row) => {
    const cells = row.querySelectorAll("td");
    data.push(Array.from(cells).map((td) => td.textContent || ""));
  });

  return data;
};

// Function to export specific table by index
const exportTableByIndex = (
  html: string,
  tableIndex: number,
  fileName: string = "table.xlsx",
): void => {
  const tableData = extractTableDataByIndex(html, tableIndex);

  if (tableData.length === 0) {
    console.warn("No table data found");
    return;
  }

  const worksheet = XLSX.utils.aoa_to_sheet(tableData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

  const colWidths = tableData[0].map((_, colIndex) => {
    return Math.max(...tableData.map((row) => (row[colIndex] || "").toString().length)) + 2;
  });

  worksheet["!cols"] = colWidths.map((width) => ({ wch: width }));

  XLSX.writeFile(workbook, fileName);
};

// Function to wrap tables with export buttons
const wrapTablesWithExportButtons = (html: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const tables = doc.querySelectorAll("table");

  tables.forEach((table, index) => {
    const wrapper = doc.createElement("div");
    wrapper.className = "table-export-wrapper mb-3";
    wrapper.setAttribute("data-table-index", index.toString());

    const buttonContainer = doc.createElement("div");
    buttonContainer.className = "flex justify-end mb-2 export-button-container";
    buttonContainer.setAttribute("data-table-index", index.toString());

    const tableClone = table.cloneNode(true);
    wrapper.appendChild(tableClone);
    wrapper.appendChild(buttonContainer);

    table.replaceWith(wrapper);
  });

  return doc.body.innerHTML;
};

// Function to parse Markdown and apply Tailwind styling
const parseMarkdownToHtml = (markdown: string, skipTableWrap?: boolean): string => {
  const html = marked.parse(markdown) as string;
  const styled = (html as string)
    .replace(/<h3>/g, '<h3 class="font-semibold text-base mt-3 mb-2 text-gray-900">')
    .replace(/<h2>/g, '<h2 class="font-bold text-lg mt-4 mb-2 text-gray-900">')
    .replace(/<h1>/g, '<h1 class="font-bold text-xl mt-5 mb-3 text-gray-900">')
    .replace(/<strong>/g, '<strong class="font-bold text-gray-900">')
    .replace(/<em>/g, '<em class="italic">')
    .replace(/<ul>/g, '<ul class="list-disc list-inside pl-2 mb-2">')
    .replace(/<ol>/g, '<ol class="list-decimal list-inside pl-2 mb-2">')
    .replace(/<li>/g, '<li class="text-gray-900 mb-1">')
    .replace(/<p>/g, '<p class="text-gray-900">')
    .replace(/<code>/g, '<code class="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">')
    .replace(/<pre>/g, '<pre class="bg-gray-100 p-2 rounded mb-2 overflow-x-auto">')
    .replace(
      /<blockquote>/g,
      '<blockquote class="border-l-4 border-gray-300 pl-3 italic text-gray-600 my-2">',
    )
    .replace(/<table>/g, '<table class="w-full border-collapse border border-gray-200 my-2">')
    .replace(/<thead>/g, '<thead class="bg-gray-100">')
    .replace(/<tbody>/g, "<tbody>")
    .replace(/<tr([^>]*)>/g, '<tr class="border border-gray-200">')
    .replace(
      /<th\b([^>]*)>/g,
      '<th class="border border-gray-200 px-3 py-2 text-left font-semibold text-gray-900 bg-gray-100">',
    )
    .replace(/<td([^>]*)>/g, () => {
      return `<td class="border border-gray-200 px-3 py-2 text-gray-900">`;
    });

  if (skipTableWrap) return styled;
  return wrapTablesWithExportButtons(styled);
};

export const MessageBubble = ({
  content,
  timestamp,
  isUser,
  isSkeleton,
  onClick,
  animate,
  onTypingTick,
  hasCharts,
}: {
  content: string;
  timestamp?: string;
  isUser?: boolean;
  isSkeleton?: boolean;
  onClick?: () => void;
  animate?: boolean;
  onTypingTick?: () => void;
  hasCharts?: boolean;
}) => {
  const t = useT();
  const contentRef = useRef<HTMLDivElement>(null);
  const onTypingTickRef = useRef(onTypingTick);
  onTypingTickRef.current = onTypingTick;
  const [thinkingIndex, setThinkingIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [displayedContent, setDisplayedContent] = useState(animate ? "" : content);

  // Thinking messages cycle (skeleton state)
  useEffect(() => {
    if (!isSkeleton) return;
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setThinkingIndex((i) => (i + 1) % THINKING_MESSAGE_COUNT);
        setVisible(true);
      }, 300);
    }, 2000);
    return () => clearInterval(interval);
  }, [isSkeleton]);

  // Typewriter animation
  useEffect(() => {
    if (!animate || !content) {
      setDisplayedContent(content);
      return;
    }

    setDisplayedContent("");
    let charIdx = 0;
    const TICK_MS = 1;
    // Aim for ~3s total; minimum 100 chars/sec for short messages
    const charsPerSec = Math.max(100, content.length / 3);
    const charsPerTick = Math.max(1, Math.ceil((charsPerSec * TICK_MS) / 1000));

    const interval = setInterval(() => {
      charIdx = Math.min(charIdx + charsPerTick, content.length);
      setDisplayedContent(content.slice(0, charIdx));
      onTypingTickRef.current?.();
      if (charIdx >= content.length) clearInterval(interval);
    }, TICK_MS);

    return () => clearInterval(interval);
  }, [animate, content]);

  const isTyping = animate === true && displayedContent.length < content.length;
  const parsedHtml = parseMarkdownToHtml(isTyping ? displayedContent : content, isTyping);

  useEffect(() => {
    if (!contentRef.current || isUser || isSkeleton || isTyping) return;

    const buttonContainers = contentRef.current.querySelectorAll(".export-button-container");

    buttonContainers.forEach((container) => {
      const tableIndex = parseInt(container.getAttribute("data-table-index") || "0", 10);

      const handleClick = () => {
        exportTableByIndex(parsedHtml, tableIndex, `table-${tableIndex + 1}.xlsx`);
      };

      const root = createRoot(container);
      root.render(
        <button
          onClick={handleClick}
          className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-white bg-btn-green hover:bg-hover-green rounded transition-colors"
          title={`Экспортировать таблицу ${tableIndex + 1} в XLSX`}
          type="button"
        >
          <IcExcel className="w-4 h-4" /> {t(Namespace.Operators, "downloadTable")}
        </button>,
      );
    });
  }, [parsedHtml, isUser, isSkeleton, isTyping]);

  return (
    <div
      className="flex gap-2 max-w-[90%] my-1"
      style={{ alignSelf: isUser ? "flex-end" : "flex-start" }}
    >
      <div className={`p-3 max-w-[100%] rounded-md bg-white text-text-primary`}>
        {isSkeleton ? (
          <span
            className="text-sm text-gray-400 transition-opacity duration-300"
            style={{ opacity: visible ? 1 : 0 }}
          >
            {t(Namespace.Chat, `thinkingMessages.${thinkingIndex}` as any)}
          </span>
        ) : (
          <div className="flex flex-col gap-1 text-sm">
            {!isUser && (
              <div className="flex justify-between items-center gap-2">
                <div className="flex items-center gap-1">
                  <img src={RaccoonBotAvatar} alt="Raccoon AI" className="w-6 h-6 rounded-full" />
                  <div
                    className={`font-semibold ${isUser ? "text-brand-green" : "text-brand-blue"}`}
                  >
                    Raccoon AI
                  </div>
                </div>

                {hasCharts && (
                  <div
                    className="text-gray-500 uppercase text-xs font-bold text-brand-blue cursor-pointer hover:underline"
                    onClick={onClick}
                  >
                    {t(Namespace.Chat, "viewChart")}
                  </div>
                )}
              </div>
            )}
            <div
              className="overflow-auto"
              ref={contentRef}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(parsedHtml) }}
            />
            {isTyping && (
              <span className="inline-block w-1.5 h-4 bg-brand-blue rounded-sm animate-pulse ml-0.5 align-middle" />
            )}
            {!isUser && !isSkeleton && (
              <div className="text-xs mt-2 italic border-t border-gray-200 pt-2">
                {t(Namespace.Chat, "testModeDisclaimer")}
              </div>
            )}
            {timestamp && (
              <div className={`flex mt-1 ${isUser ? "justify-end" : "justify-start"}`}>
                <span className="text-xs text-gray-400">
                  {format(new Date(timestamp), "HH:mm")}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
